import React, { useCallback, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
    FlexDiv,
    TextDefault,
    TextInput,
    TextTiny,
} from '@awarego-ui/components';
import {
    buildRruleString,
    defaultNextOccurrence,
    parseRruleToForm,
    weekdayCodeFromDate,
    toTimeKey,
} from '../../../../utils/assessmentRemindersRrule';

const WEEKDAYS = [
    { code: 'MO', label: 'Mon' },
    { code: 'TU', label: 'Tue' },
    { code: 'WE', label: 'Wed' },
    { code: 'TH', label: 'Thu' },
    { code: 'FR', label: 'Fri' },
    { code: 'SA', label: 'Sat' },
    { code: 'SU', label: 'Sun' },
];

function RecurringRemindersBuilder({
    initialRrule,
    initialDtstart,
    deliveryDate,
    dueDate,
    futureDates,
    sentDates,
    timeZone,
    onChange,
}) {
    const defaults = useMemo(() => {
        const parsed = parseRruleToForm(initialRrule);
        const start = initialDtstart
            ? moment(initialDtstart)
            : defaultNextOccurrence({
                  deliveryDate,
                  dueDate,
                  futureDates,
                  sentDates,
              });
        const weekday = weekdayCodeFromDate(start);
        return {
            freq: parsed?.freq || 'WEEKLY',
            interval: parsed?.interval || 1,
            byweekday:
                parsed?.byweekday?.length > 0
                    ? parsed.byweekday
                    : weekday
                      ? [weekday]
                      : ['MO'],
            endType: parsed?.endType || 'never',
            count: parsed?.count || 10,
            until: parsed?.until || (dueDate ? moment(dueDate) : null),
            start,
        };
    }, [
        deliveryDate,
        dueDate,
        futureDates,
        initialDtstart,
        initialRrule,
        sentDates,
    ]);

    const [freq, setFreq] = useState(defaults.freq);
    const [interval, setInterval] = useState(defaults.interval);
    const [byweekday, setByweekday] = useState(defaults.byweekday);
    const [endType, setEndType] = useState(defaults.endType);
    const [count, setCount] = useState(defaults.count);
    const [until, setUntil] = useState(defaults.until);
    const [start, setStart] = useState(defaults.start);
    const [showRrule, setShowRrule] = useState(false);
    const [rruleText, setRruleText] = useState(initialRrule || '');
    const [rruleError, setRruleError] = useState(null);
    const lastEmitRef = useRef({
        rrule: initialRrule || '',
        dtstartKey: initialDtstart ? toTimeKey(initialDtstart) : null,
    });

    const emit = useCallback(
        (next) => {
            const rule = buildRruleString({
                freq: next.freq,
                interval: next.interval,
                byweekday: next.freq === 'WEEKLY' ? next.byweekday : [],
                until:
                    next.endType === 'on_date' && next.until
                        ? next.until.toDate()
                        : null,
                count: next.endType === 'after' ? next.count : null,
            });
            const dtstart = next.start?.toDate
                ? next.start.toDate()
                : next.start;
            const dtstartIso = dtstart
                ? new Date(dtstart).toISOString()
                : null;
            const dtstartKey = toTimeKey(dtstartIso);
            setRruleText(rule);
            setRruleError(null);
            if (
                rule === lastEmitRef.current.rrule &&
                dtstartKey === lastEmitRef.current.dtstartKey
            ) {
                return;
            }
            lastEmitRef.current = { rrule: rule, dtstartKey };
            onChange({
                rrule: rule,
                dtstart: dtstartIso,
                error: null,
            });
        },
        [onChange]
    );

    const update = (patch) => {
        const next = {
            freq,
            interval,
            byweekday,
            endType,
            count,
            until,
            start,
            ...patch,
        };
        if (patch.freq !== undefined) setFreq(patch.freq);
        if (patch.interval !== undefined) setInterval(patch.interval);
        if (patch.byweekday !== undefined) setByweekday(patch.byweekday);
        if (patch.endType !== undefined) setEndType(patch.endType);
        if (patch.count !== undefined) setCount(patch.count);
        if (patch.until !== undefined) setUntil(patch.until);
        if (patch.start !== undefined) setStart(patch.start);
        emit(next);
    };

    const toggleWeekday = (code) => {
        const next = byweekday.includes(code)
            ? byweekday.filter((c) => c !== code)
            : [...byweekday, code];
        if (next.length === 0) return;
        update({ byweekday: next });
    };

    const onRruleBlur = () => {
        const parsed = parseRruleToForm(rruleText);
        if (!parsed) {
            setRruleError('This RRULE is not valid');
            onChange({
                rrule: rruleText,
                dtstart: start ? start.toDate().toISOString() : null,
                error: 'This RRULE is not valid',
            });
            return;
        }
        setRruleError(null);
        update({
            freq: parsed.freq,
            interval: parsed.interval,
            byweekday:
                parsed.byweekday.length > 0
                    ? parsed.byweekday
                    : byweekday,
            endType: parsed.endType,
            count: parsed.count,
            until: parsed.until || until,
        });
    };

    const intervalUnit =
        freq === 'DAILY'
            ? interval === 1
                ? 'day'
                : 'days'
            : freq === 'WEEKLY'
              ? interval === 1
                  ? 'week'
                  : 'weeks'
              : interval === 1
                ? 'month'
                : 'months';

    return (
        <FlexDiv column gap={16}>
            <TextInput
                select
                label="Frequency"
                value={freq}
                onChange={(e) => update({ freq: e.target.value })}
                stretched
            >
                <MenuItem value="DAILY">Daily</MenuItem>
                <MenuItem value="WEEKLY">Weekly</MenuItem>
                <MenuItem value="MONTHLY">Monthly</MenuItem>
            </TextInput>
            <TextInput
                label={`Every (${intervalUnit})`}
                type="number"
                value={interval}
                onChange={(e) =>
                    update({
                        interval: Math.max(1, Number(e.target.value) || 1),
                    })
                }
                inputProps={{ min: 1, max: 99 }}
                stretched
            />

            {freq === 'WEEKLY' && (
                <FlexDiv column gap={8}>
                    <TextTiny lighter>On days</TextTiny>
                    <FlexDiv gap={8}>
                        {WEEKDAYS.map((day) => (
                            <Chip
                                key={day.code}
                                label={day.label}
                                color={
                                    byweekday.includes(day.code)
                                        ? 'primary'
                                        : 'default'
                                }
                                variant={
                                    byweekday.includes(day.code)
                                        ? 'filled'
                                        : 'outlined'
                                }
                                onClick={() => toggleWeekday(day.code)}
                            />
                        ))}
                    </FlexDiv>
                </FlexDiv>
            )}

            <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                    label={`Next occurrence [${timeZone || ''}]`}
                    value={start}
                    onChange={(date) => {
                        if (!date || !date.isValid()) return;
                        if (start && date.isSame(start, 'minute')) return;
                        update({ start: date });
                    }}
                    ampm={false}
                    disablePast
                    format={`DD/MM/YYYY HH:mm[${timeZone || ''}]`}
                    minDateTime={moment()}
                    maxDateTime={dueDate ? moment(dueDate) : undefined}
                    slotProps={{
                        textField: {
                            helperText:
                                'Start of the next future reminder. Recurrence never rewinds over sent dates.',
                        },
                    }}
                />
            </LocalizationProvider>

            <FlexDiv column gap={8}>
                <TextTiny lighter>Ends</TextTiny>
                <RadioGroup
                    value={endType}
                    onChange={(e) => update({ endType: e.target.value })}
                >
                    <FormControlLabel
                        value="never"
                        control={<Radio />}
                        label={
                            dueDate
                                ? `Assessment due: ${moment(dueDate).format(
                                      `ll LT [${timeZone || ''}]`
                                  )}`
                                : 'Assessment due'
                        }
                    />
                    <FormControlLabel
                        value="after"
                        control={<Radio />}
                        label="After a number of occurrences"
                    />
                    {endType === 'after' && (
                        <TextInput
                            label="Occurrences"
                            type="number"
                            value={count}
                            onChange={(e) =>
                                update({
                                    count: Math.max(
                                        1,
                                        Number(e.target.value) || 1
                                    ),
                                })
                            }
                            inputProps={{ min: 1, max: 52 }}
                            stretched
                        />
                    )}
                    <FormControlLabel
                        value="on_date"
                        control={<Radio />}
                        label="On date"
                    />
                </RadioGroup>
                {endType === 'on_date' && (
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                            label={`End on [${timeZone || ''}]`}
                            value={until}
                            onChange={(date) => date && update({ until: date })}
                            ampm={false}
                            format={`DD/MM/YYYY HH:mm[${timeZone || ''}]`}
                            maxDateTime={dueDate ? moment(dueDate) : undefined}
                        />
                    </LocalizationProvider>
                )}
            </FlexDiv>

            <TextDefault
                link
                themed
                bold
                onClick={() => setShowRrule((v) => !v)}
            >
                {showRrule ? 'Hide RRULE' : 'Show RRULE'}
            </TextDefault>
            {showRrule && (
                <TextInput
                    label="RRULE"
                    value={rruleText}
                    onChange={(e) => setRruleText(e.target.value)}
                    onBlur={onRruleBlur}
                    error={Boolean(rruleError)}
                    helperText={
                        rruleError ||
                        'Edit and leave the field to apply. Invalid rules are not applied to the form.'
                    }
                    stretched
                />
            )}
        </FlexDiv>
    );
}

export default observer(RecurringRemindersBuilder);
